import React, { useState } from 'react';
import { Download, Copy, Check, Camera, Image, Layers } from 'lucide-react';
import type { ExportStyle } from '@/utils/overlayExport';

interface ExportControlsProps {
  onDownload: () => Promise<void> | void;
  onCopy: () => Promise<void> | void;
  exportStyle: ExportStyle;
  onStyleToggle: () => void;
  disabled?: boolean;
  /** Hide the styled/transparent toggle button (default true) */
  showStyleToggle?: boolean;
}

const ExportControls: React.FC<ExportControlsProps> = ({
  onDownload,
  onCopy,
  exportStyle,
  onStyleToggle,
  disabled = false,
  showStyleToggle = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const isMobile = typeof window !== 'undefined' && 'ontouchstart' in window;

  const handleDownload = async () => {
    setSaving(true);
    try {
      await onDownload();
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    await onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-1.5">
      {/* Style toggle */}
      {showStyleToggle && (
        <button
          onClick={onStyleToggle}
          disabled={disabled}
          className="p-1.5 rounded-lg cgm-button transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
          title={exportStyle === 'styled' ? 'Switch to transparent' : 'Switch to styled'}
        >
          {exportStyle === 'styled' ? (
            <Image className="w-3.5 h-3.5 cgm-text-secondary" />
          ) : (
            <Layers className="w-3.5 h-3.5 cgm-text-secondary" />
          )}
        </button>
      )}

      {/* Download / Save */}
      <button
        onClick={handleDownload}
        disabled={disabled || saving}
        className="p-1.5 rounded-lg cgm-button transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
        title={isMobile ? 'Save to Photos' : 'Download PNG'}
      >
        {isMobile ? (
          <Camera className="w-3.5 h-3.5 cgm-text-secondary" />
        ) : (
          <Download className="w-3.5 h-3.5 cgm-text-secondary" />
        )}
      </button>

      {/* Copy to clipboard */}
      <button
        onClick={handleCopy}
        disabled={disabled}
        className="p-1.5 rounded-lg cgm-button transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <Copy className="w-3.5 h-3.5 cgm-text-secondary" />
        )}
      </button>
    </div>
  );
};

export default ExportControls;
